import { useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../hooks/useAuth';

export default function KeywordMagicPage() {
  const [keyword, setKeyword] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [filter, setFilter] = useState('all');
  const { updateCredits } = useAuth();

  async function analyze() {
    if (!keyword.trim() || loading) return;
    setError(''); setLoading(true); setData(null);
    try {
      const res = await api.post('/keywords/magic', { keyword });
      setData(res.data.data);
      updateCredits(res.data.credits_remaining);
    } catch (e) { setError(e.response?.data?.error || 'Failed'); } finally { setLoading(false); }
  }

  const filtered = data ? (filter === 'all' ? data.keywords : data.keywords.filter(k => k.type === filter)) : [];

  return (
    <div className="fade-up">
      <div className="page-header">
        <h1 className="page-title">Keyword Magic Tool</h1>
        <p className="page-sub">Generate 200+ keyword ideas from one seed keyword — like SEMrush Magic Tool · 5 credits</p>
      </div>

      <div className="card" style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10 }}>
          <input className="input input-lg" placeholder="Seed keyword e.g. seo tools, protein powder, running shoes" value={keyword} onChange={e => setKeyword(e.target.value)} onKeyDown={e => e.key === 'Enter' && analyze()} />
          <button className="btn btn-orange btn-lg" onClick={analyze} disabled={loading || !keyword.trim()}>{loading ? 'Generating...' : 'Generate →'}</button>
        </div>
        {error && <div style={{ marginTop: 10, color: '#FF3D57', fontSize: 13, background: '#FFEBEE', padding: '8px 12px', borderRadius: 6 }}>{error}</div>}
      </div>

      {loading && <div className="card"><div className="skeleton" style={{ height: 300 }} /></div>}

      {data && (
        <>
          <div className="metric-grid">
            <div className="card" style={{ margin: 0 }}><div className="metric-label">Total Keywords</div><div className="metric-value">{data.total}</div><div className="metric-sub">unique ideas</div></div>
            <div className="card" style={{ margin: 0 }}><div className="metric-label">Total Volume</div><div className="metric-value">{(data.metrics.totalVolume/1000).toFixed(1)}K</div><div className="metric-sub">combined volume</div></div>
            <div className="card" style={{ margin: 0 }}><div className="metric-label">Avg Volume</div><div className="metric-value">{data.metrics.avgVolume.toLocaleString()}</div><div className="metric-sub">avg per keyword</div></div>
            <div className="card" style={{ margin: 0 }}><div className="metric-label">Avg KD</div><div className="metric-value">{data.metrics.avgKD}%</div><div className="metric-sub">avg difficulty</div></div>
          </div>

          <div className="card">
            <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
              {[
                { id: 'all', label: `All (${data.total})` },
                { id: 'Broad Match', label: `Broad (${data.groups['Broad Match']})` },
                { id: 'Phrase Match', label: `Phrase (${data.groups['Phrase Match']})` },
                { id: 'Questions', label: `Questions (${data.groups['Questions']})` },
                { id: 'Related', label: `Related (${data.groups['Related']})` },
              ].map(f => (
                <button key={f.id} onClick={() => setFilter(f.id)} className={`btn btn-sm ${filter === f.id ? 'btn-black' : 'btn-white'}`}>{f.label}</button>
              ))}
            </div>

            <div className="table-wrap">
              <table className="table">
                <thead><tr><th>Keyword</th><th>Type</th><th>Volume</th><th>KD %</th><th>CPC</th><th>Intent</th><th>Trend</th></tr></thead>
                <tbody>
                  {filtered.slice(0, 100).map((k,i) => (
                    <tr key={i}>
                      <td style={{ fontWeight: 600, maxWidth: 300 }}>{k.keyword}</td>
                      <td><span className="badge badge-gray" style={{ fontSize: 10 }}>{k.type}</span></td>
                      <td>{k.volume.toLocaleString()}</td>
                      <td><span className={`badge ${k.kd < 30 ? 'badge-green' : k.kd < 60 ? 'badge-yellow' : 'badge-red'}`}>{k.kd}%</span></td>
                      <td>${k.cpc}</td>
                      <td><span className="badge badge-gray" style={{ fontSize: 10 }}>{k.intent}</span></td>
                      <td style={{ color: parseFloat(k.trend) > 0 ? '#00C853' : '#FF3D57', fontWeight: 600, fontSize: 12 }}>{parseFloat(k.trend) > 0 ? '↗' : '↘'} {k.trend}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {filtered.length > 100 && <div style={{ textAlign: 'center', padding: 12, color: '#6B7280', fontSize: 12 }}>Showing 100 of {filtered.length} keywords · Export to CSV coming soon</div>}
          </div>
        </>
      )}
    </div>
  );
}
